import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faVoteYea, faCheckCircle, faHourglassHalf, faUsers,
  faExclamationTriangle, faSpinner, faChurch, faWifi,
  faRightFromBracket, faMobileScreenButton, faCircleInfo,
  faBell, faBellSlash, faCircleCheck, faRotate
} from '@fortawesome/free-solid-svg-icons';
import io from 'socket.io-client';
import { getVoteState, voteCheckin, voteCheckout, castVote } from '../services/api';
import {
  unlockAudio, playAlarm, isSoundOn, setSoundOn,
  requestWakeLock, releaseWakeLock
} from '../utils/voteAlarm';
import './Vote.css';

// Lembrete sonoro enquanto a votação está aberta e a pessoa ainda não votou
const REMINDER_MS = 30 * 1000;
// Depois de quanto tempo sem resposta do servidor consideramos "sem internet"
const OFFLINE_AFTER_MS = 15 * 1000;

const TOKEN_KEY = 'voteDeviceToken';

// Recupera o token anônimo deste navegador (null se nunca abriu a votação aqui)
function readDeviceToken() {
  return localStorage.getItem(TOKEN_KEY);
}

// Gera/recupera um token anônimo por navegador (só para impedir voto duplo)
function ensureDeviceToken() {
  let t = readDeviceToken();
  if (!t) {
    t = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(TOKEN_KEY, t);
  }
  return t;
}

// Marca (por sessão/código) que a pessoa saiu da assembleia neste navegador,
// para que um recarregamento da página não refaça o check-in sozinho.
const leftKey = (c) => `voteLeft_${c}`;
// Sessão de presença em que este navegador fez check-in (muda a cada novo dia da assembleia)
const sessionKey = (c) => `voteSession_${c}`;

// Detecta navegador embutido de apps (WhatsApp, Instagram, Facebook...).
// Detecção "melhor esforço": nem todo app se identifica no user-agent.
function detectInAppBrowser() {
  const ua = navigator.userAgent || '';
  if (/WhatsApp/i.test(ua)) return 'WhatsApp';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return 'Facebook';
  if (/Messenger/i.test(ua)) return 'Messenger';
  if (/\bLine\//i.test(ua)) return 'Line';
  if (/Telegram/i.test(ua)) return 'Telegram';
  return null;
}

const Vote = () => {
  const params = new URLSearchParams(window.location.search);
  const codeFromUrl = (params.get('code') || '').toUpperCase();

  const [code, setCode] = useState(codeFromUrl);
  const [codeInput, setCodeInput] = useState(codeFromUrl);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(!!codeFromUrl);
  const [error, setError] = useState('');
  const [checkedIn, setCheckedIn] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [justVoted, setJustVoted] = useState(false);
  const [connected, setConnected] = useState(false);
  // 'ask'   = navegador novo: pergunta se é a primeira vez neste celular
  // 'other' = disse que já abriu em outro app/navegador: orienta antes de contar
  // 'ready' = participando normalmente (check-in feito)
  // 'left'  = saiu da assembleia neste navegador
  const [entryStep, setEntryStep] = useState(() => {
    if (codeFromUrl && localStorage.getItem(leftKey(codeFromUrl))) return 'left';
    return readDeviceToken() ? 'ready' : 'ask';
  });
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // Alarme: preferência de som e aviso em destaque ("votação aberta" / "a secretaria chama")
  const [soundOn, setSoundOnState] = useState(isSoundOn());
  const [voteAlert, setAlert] = useState(null); // { kind: 'open' | 'nudge', at: number } | null
  // Momento da última resposta OK do servidor (para a faixa de status)
  const [lastOkAt, setLastOkAt] = useState(0);
  const [now, setNow] = useState(Date.now());

  const token = useRef(readDeviceToken());
  const socketRef = useRef(null);
  const pollRef = useRef(null);
  // undefined = ainda não carregou nenhum estado; null = carregou e não há votação aberta
  const currentRoundRef = useRef(undefined);
  const checkinInFlight = useRef(false);
  const inAppBrowser = useRef(detectInAppBrowser()).current;
  const isLeft = entryStep === 'left';
  // Estado mais recente acessível de dentro dos handlers do socket
  const stateRef = useRef(null);
  const justVotedRef = useRef(false);

  const fetchState = useCallback(async (theCode) => {
    try {
      const { data } = await getVoteState(theCode, token.current);
      setLastOkAt(Date.now());
      // Nova sessão de presença (ex.: segundo dia): a presença antiga foi zerada,
      // então este navegador precisa marcar presença de novo.
      const sid = String(data.sessionId || 0);
      const savedSid = localStorage.getItem(sessionKey(theCode));
      if (savedSid !== null && savedSid !== sid) {
        localStorage.setItem(sessionKey(theCode), sid);
        localStorage.removeItem(leftKey(theCode));
        setCheckedIn(false);
        setEntryStep(prev => (prev === 'left' ? 'ready' : prev));
      }
      stateRef.current = data;
      setState(data);
      setError('');
      const key = data.voting
        ? `${data.voting.electionMinistryId}:${data.voting.round}:${data.voting.roundId || ''}`
        : null;
      if (key !== currentRoundRef.current) {
        const hadPrevious = currentRoundRef.current !== undefined;
        currentRoundRef.current = key;
        setSelectedCandidates([]);
        setJustVoted(false);
        justVotedRef.current = false;
        if (key && !data.alreadyVoted) {
          // Nova votação aberta e este celular ainda não votou: alarme + aviso.
          // (Na primeira carga da página toca mais suave; quando abre ao vivo, toca forte.)
          playAlarm({ strong: hadPrevious });
          setAlert({ kind: 'open', at: Date.now() });
        } else {
          setAlert(null);
        }
      } else if (data.alreadyVoted || !key) {
        setAlert(null);
      }
      return data;
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Código inválido. Confira o código exibido no telão.');
        setState(null);
      }
      return null;
    }
  }, []);

  // Conexão (estado + tempo real). Não roda depois que a pessoa saiu.
  useEffect(() => {
    if (!code) return;
    let active = true;

    (async () => {
      setLoading(true);
      const data = await fetchState(code);
      if (!active) return;
      setLoading(false);
      if (!data) return;

      const serverUrl = process.env.REACT_APP_API_URL || window.location.origin;
      socketRef.current = io(serverUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000
      });
      socketRef.current.on('connect', () => {
        setConnected(true);
        if (data.electionId) socketRef.current.emit('election:join', data.electionId);
      });
      socketRef.current.on('disconnect', () => setConnected(false));
      socketRef.current.on('election:state', () => fetchState(code));
      socketRef.current.on('round:opened', () => fetchState(code));
      socketRef.current.on('round:closed', () => fetchState(code));
      socketRef.current.on('round:cancelled', () => fetchState(code));
      socketRef.current.on('vote:count', () => fetchState(code));
      socketRef.current.on('checkin:count', () => fetchState(code));
      // Secretaria chamou quem ainda não votou: só toca em quem NÃO votou nesta rodada
      socketRef.current.on('vote:nudge', (n) => {
        const s = stateRef.current;
        const voting = s && s.voting;
        if (!voting || isLeft) return;
        if (n && n.electionMinistryId && String(n.electionMinistryId) !== String(voting.electionMinistryId)) return;
        if (s.alreadyVoted || justVotedRef.current) return;
        playAlarm({ strong: true });
        setAlert({ kind: 'nudge', at: Date.now() });
        requestWakeLock();
      });

      // fallback polling: 3s participando; 20s depois de sair (só para perceber um novo dia)
      pollRef.current = setInterval(() => fetchState(code), isLeft ? 20000 : 3000);
    })();

    // Ao desbloquear o celular / voltar para a aba: atualiza na hora (presença + votação)
    const onWake = () => {
      if (document.visibilityState === 'hidden') return;
      fetchState(code);
      if (socketRef.current && !socketRef.current.connected) socketRef.current.connect();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    window.addEventListener('pageshow', onWake);

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
      window.removeEventListener('pageshow', onWake);
      if (socketRef.current) socketRef.current.disconnect();
      if (pollRef.current) clearInterval(pollRef.current);
      socketRef.current = null;
      pollRef.current = null;
    };
  }, [code, fetchState, isLeft]);

  // Relógio para a faixa de status ("sem resposta há X s")
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  // Enquanto a votação está aberta e esta pessoa não votou:
  // mantém a tela acesa e repete um lembrete curto a cada REMINDER_MS.
  const votingOpen = !!state?.voting;
  const votedNow = !!(state?.alreadyVoted || justVoted);
  const needsVote = votingOpen && !votedNow && entryStep === 'ready';
  useEffect(() => {
    if (!needsVote) {
      releaseWakeLock();
      return undefined;
    }
    requestWakeLock();
    const reacquire = () => { if (document.visibilityState === 'visible') requestWakeLock(); };
    document.addEventListener('visibilitychange', reacquire);
    const t = setInterval(() => playAlarm({ strong: false }), REMINDER_MS);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', reacquire);
      releaseWakeLock();
    };
  }, [needsVote]);

  // Título da aba pisca enquanto falta votar (ajuda quem está em outra aba)
  useEffect(() => {
    const original = document.title;
    if (!needsVote) return undefined;
    let on = false;
    const t = setInterval(() => {
      on = !on;
      document.title = on ? '🔔 VOTE AGORA' : original;
    }, 1000);
    return () => { clearInterval(t); document.title = original; };
  }, [needsVote]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setSoundOnState(next);
    if (next) {
      unlockAudio();
      playAlarm({ strong: false });
    }
  };

  // Check-in (marca presença) só quando a pessoa está "pronta":
  // navegador que já participava, ou confirmou que é a primeira vez.
  useEffect(() => {
    if (!code || entryStep !== 'ready' || !state || checkedIn || checkinInFlight.current) return;
    checkinInFlight.current = true;
    (async () => {
      try {
        token.current = ensureDeviceToken();
        await voteCheckin(code, token.current);
        localStorage.setItem(sessionKey(code), String(state.sessionId || 0));
        setCheckedIn(true);
        fetchState(code);
      } catch (e) { /* ignora — tenta de novo no próximo estado */ }
      finally {
        checkinInFlight.current = false;
      }
    })();
  }, [code, entryStep, state, checkedIn, fetchState]);

  const handleSubmitCode = (e) => {
    e.preventDefault();
    const c = codeInput.trim().toUpperCase();
    if (c.length < 4) {
      setError('Digite o código de 4 caracteres do telão.');
      return;
    }
    setError('');
    unlockAudio(); // gesto do usuário: libera o som do alarme
    if (localStorage.getItem(leftKey(c))) setEntryStep('left');
    setCode(c);
  };

  // "É a primeira vez neste celular" / "Quero usar este navegador mesmo assim"
  const handleJoin = () => {
    unlockAudio(); // gesto do usuário: libera o som do alarme
    if (code) localStorage.removeItem(leftKey(code));
    setConfirmLeave(false);
    setCheckedIn(false);
    setEntryStep('ready');
  };

  // Sair da assembleia: remove a presença deste navegador.
  // O token é mantido de propósito: se a pessoa entrar de novo na mesma
  // rodada, continua impedida de votar duas vezes.
  const handleLeave = async () => {
    setLeaving(true);
    setError('');
    try {
      if (token.current) await voteCheckout(code, token.current);
      localStorage.setItem(leftKey(code), '1');
      setCheckedIn(false);
      setConfirmLeave(false);
      setEntryStep('left');
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível sair agora. Verifique a internet e tente de novo.');
    } finally {
      setLeaving(false);
    }
  };

  const toggleCandidate = (candidateId, maxVotes) => {
    setSelectedCandidates((prev) => {
      if (prev.includes(candidateId)) {
        return prev.filter((id) => id !== candidateId);
      }
      if (maxVotes > 1) {
        if (prev.length >= maxVotes) return prev; // não passa do limite
        return [...prev, candidateId];
      }
      return [candidateId]; // vaga única: substitui a seleção
    });
  };

  const handleVote = async () => {
    if (selectedCandidates.length === 0 || !state?.voting) return;
    setSubmitting(true);
    setError('');
    try {
      await castVote(code, token.current, selectedCandidates);
      setJustVoted(true);
      justVotedRef.current = true;
      setAlert(null);
      setState((prev) => ({ ...prev, alreadyVoted: true }));
    } catch (err) {
      if (err.response?.status === 409) {
        setJustVoted(true);
        justVotedRef.current = true;
        setAlert(null);
        setState((prev) => ({ ...prev, alreadyVoted: true }));
      } else {
        setError(err.response?.data?.message || 'Não foi possível registrar seu voto. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Render ----------

  // 1) Sem código válido → pede o código
  if (!code || (error && !state)) {
    return (
      <div className="vote-page">
        <div className="vote-card">
          <div className="vote-brand">
            <FontAwesomeIcon icon={faVoteYea} />
            <h1>Votação</h1>
          </div>
          <p className="vote-help">
            Digite o <strong>código da sessão</strong> exibido no telão para participar.
          </p>
          <form onSubmit={handleSubmitCode} className="vote-code-form">
            <input
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              maxLength={4}
              className="vote-code-input"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              placeholder="Ex.: 7B3K"
              aria-label="Código da sessão"
            />
            <button type="submit" className="vote-btn vote-btn-primary vote-btn-block">
              <FontAwesomeIcon icon={faCheckCircle} /> Entrar
            </button>
          </form>
          {error && <p className="vote-error"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</p>}
        </div>
      </div>
    );
  }

  // Aviso para navegador embutido (WhatsApp, Instagram...)
  const inAppBanner = inAppBrowser ? (
    <div className="vote-inapp">
      <FontAwesomeIcon icon={faMobileScreenButton} />
      <span>
        Você abriu pelo <strong>{inAppBrowser}</strong>. Continue votando <strong>por aqui</strong>.
        Se preferir o Chrome/Safari, toque em <strong>“Sair da assembleia”</strong> antes de trocar
        — senão você conta como duas pessoas.
      </span>
    </div>
  ) : null;

  // 2) Saiu da assembleia neste navegador
  if (isLeft) {
    return (
      <div className="vote-page">
        <div className="vote-card vote-center">
          <FontAwesomeIcon icon={faRightFromBracket} className="vote-big-icon" />
          <h2 className="vote-step-title">Você saiu da assembleia</h2>
          <p className="vote-help">
            Sua presença foi removida da contagem. Obrigado por participar!
          </p>
          <button className="vote-btn vote-btn-primary vote-btn-block" onClick={handleJoin}>
            <FontAwesomeIcon icon={faCheckCircle} /> Entrar novamente
          </button>
        </div>
        <p className="vote-footer">Voto secreto e anônimo</p>
      </div>
    );
  }

  // 3) Navegador novo: pergunta antes de contar a presença
  if (entryStep === 'ask' && state) {
    return (
      <div className="vote-page">
        <div className="vote-card">
          <div className="vote-brand">
            <FontAwesomeIcon icon={faVoteYea} />
            <h1>{state.title || 'Votação'}</h1>
          </div>
          {inAppBanner}
          <p className="vote-help">
            É a <strong>primeira vez</strong> que você abre a votação <strong>neste celular</strong>?
          </p>
          <p className="vote-hint">
            <FontAwesomeIcon icon={faCircleInfo} /> Se você <strong>já abriu</strong> antes e acha que caiu:
            toque em <strong>Não</strong> — provavelmente você ainda está conectado lá.
            Abrir de novo faria você contar como <strong>duas pessoas</strong>.
          </p>
          <div className="vote-choice">
            <button className="vote-btn vote-btn-primary vote-btn-block" onClick={handleJoin}>
              <FontAwesomeIcon icon={faCheckCircle} /> Sim, é a primeira vez
            </button>
            <button className="vote-btn vote-btn-secondary vote-btn-block" onClick={() => setEntryStep('other')}>
              Não, já abri em outro app/navegador
            </button>
          </div>
        </div>
        <p className="vote-footer">Voto secreto e anônimo</p>
      </div>
    );
  }

  // 4) Já abriu em outro lugar: orienta para não contar duas vezes
  if (entryStep === 'other' && state) {
    return (
      <div className="vote-page">
        <div className="vote-card">
          <div className="vote-brand">
            <FontAwesomeIcon icon={faCircleInfo} />
            <h1>Para não contar duas vezes</h1>
          </div>
          <ol className="vote-steps">
            <li>Volte para onde você abriu primeiro (ex.: <strong>WhatsApp</strong>, Chrome, Safari) e continue <strong>por lá</strong>.</li>
            <li>Se quiser mesmo usar <strong>este</strong> navegador: lá no primeiro, toque em <strong>“Sair da assembleia”</strong> (no fim da tela) e depois volte aqui.</li>
          </ol>
          <div className="vote-choice">
            <button className="vote-btn vote-btn-primary vote-btn-block" onClick={handleJoin}>
              Já saí do outro, usar este
            </button>
            <button className="vote-btn vote-btn-secondary vote-btn-block" onClick={() => setEntryStep('ask')}>
              Voltar
            </button>
          </div>
        </div>
        <p className="vote-footer">Voto secreto e anônimo</p>
      </div>
    );
  }

  // 5) Carregando
  if (loading) {
    return (
      <div className="vote-page">
        <div className="vote-card vote-center">
          <FontAwesomeIcon icon={faSpinner} spin className="vote-big-icon" />
          <p>Conectando…</p>
        </div>
      </div>
    );
  }

  const voting = state?.voting;
  const alreadyVoted = state?.alreadyVoted || justVoted;

  // Faixa de status: o servidor respondeu há pouco? (mais confiável que só o socket)
  const sinceOk = lastOkAt ? now - lastOkAt : Infinity;
  const serverOk = sinceOk < OFFLINE_AFTER_MS;
  const connStatus = serverOk && connected ? 'on' : serverOk ? 'slow' : 'off';
  const connLabel = {
    on: <><strong>Conectado.</strong> Sua presença está contando — <strong>não precisa ler o QR de novo</strong>.</>,
    slow: <><strong>Conectado</strong> (atualizando a cada poucos segundos). Mantenha esta tela aberta; não leia o QR de novo.</>,
    off: <><strong>Sem resposta do servidor.</strong> Verifique Wi‑Fi/dados e mantenha esta tela aberta — ela reconecta sozinha. Não leia o QR de novo.</>
  }[connStatus];
  const connIcon = connStatus === 'on' ? faCircleCheck : connStatus === 'slow' ? faWifi : faRotate;

  return (
    <div className="vote-page">
      <div className="vote-card">
        <div className="vote-topbar">
          <span className="vote-topbar-title">
            <FontAwesomeIcon icon={faVoteYea} /> {state?.title || 'Votação'}
          </span>
          <button
            type="button"
            className={`vote-sound-btn ${soundOn ? 'on' : 'off'}`}
            onClick={toggleSound}
            title={soundOn ? 'Som ligado: o celular apita quando a votação abrir. Toque para silenciar.' : 'Som desligado (só vibração/aviso na tela). Toque para ligar.'}
            aria-label={soundOn ? 'Desligar som do alarme' : 'Ligar som do alarme'}
          >
            <FontAwesomeIcon icon={soundOn ? faBell : faBellSlash} />
            <span>{soundOn ? 'Som' : 'Mudo'}</span>
          </button>
        </div>

        <div className={`vote-status-bar is-${connStatus}`} role="status">
          <FontAwesomeIcon icon={connIcon} spin={connStatus === 'off'} />
          <span>{connLabel}</span>
        </div>

        {inAppBanner}

        <div className="vote-presence">
          <FontAwesomeIcon icon={faUsers} />
          <span>{state?.checkins || 0}{state?.expectedVoters ? ` / ${state.expectedVoters}` : ''} presentes</span>
          {checkedIn && (
            <span className="vote-presence-you" title="Sua presença foi registrada">
              <FontAwesomeIcon icon={faCheckCircle} /> você está aqui
            </span>
          )}
        </div>

        {/* Aviso em destaque: votação abriu / secretaria chamou (só enquanto não votou) */}
        {voteAlert && voting && !alreadyVoted && (
          <div className={`vote-alert ${voteAlert.kind === 'nudge' ? 'is-nudge' : ''}`} role="alert">
            <FontAwesomeIcon icon={faBell} shake />
            <span>
              {voteAlert.kind === 'nudge'
                ? <><strong>A secretaria pede que você vote agora.</strong> Faltam poucos votos para encerrar.</>
                : <><strong>Votação aberta!</strong> Escolha abaixo e confirme seu voto.</>}
            </span>
          </div>
        )}

        {/* Sem votação aberta */}
        {!voting && (
          <div className="vote-waiting">
            <FontAwesomeIcon icon={faHourglassHalf} className="vote-big-icon pulse" />
            <h2>Aguarde</h2>
            {state?.featured && state.featured.view === 'names' ? (
              <p>
                Em discussão: <strong>{state.featured.name}</strong>.<br />
                Acompanhe os nomes no telão. A votação abrirá nesta tela automaticamente.
              </p>
            ) : (
              <p>A secretaria vai abrir a próxima votação em instantes. Esta tela atualiza sozinha.</p>
            )}
            <p className="vote-hint">
              <FontAwesomeIcon icon={faMobileScreenButton} /> Pode bloquear o celular. Ao desbloquear, esta tela
              volta sozinha e <strong>apita quando a votação abrir</strong>. Não leia o QR de novo.
            </p>
          </div>
        )}

        {/* Votação aberta e já votou */}
        {voting && alreadyVoted && (
          <div className="vote-done">
            <FontAwesomeIcon icon={faCheckCircle} className="vote-big-icon success" />
            <h2>Voto registrado</h2>
            <p>Obrigado! Aguarde o resultado no telão.</p>
            <div className="vote-count-chip">
              <FontAwesomeIcon icon={faUsers} /> {voting.votesCount} já votaram
            </div>
          </div>
        )}

        {/* Votação aberta e ainda não votou */}
        {voting && !alreadyVoted && (() => {
          const maxVotes = voting.votesPerVoter || 1;
          const multi = maxVotes > 1;
          const atLimit = multi && selectedCandidates.length >= maxVotes;
          const remaining = Math.max(0, maxVotes - selectedCandidates.length);
          // Texto dos votos restantes (várias vagas)
          const remainingMsg = remaining === 0
            ? `Você usou todos os seus ${maxVotes} votos`
            : selectedCandidates.length === 0
              ? `Você tem ${maxVotes} votos`
              : `Faltam ${remaining} ${remaining === 1 ? 'voto' : 'votos'}`;
          const remainingBox = (
            <div className={`vote-remaining ${remaining === 0 ? 'is-full' : ''}`}>
              <span className="vote-remaining-main">
                <FontAwesomeIcon icon={remaining === 0 ? faCheckCircle : faVoteYea} /> {remainingMsg}
              </span>
              <span className="vote-remaining-sub">
                {selectedCandidates.length} de {maxVotes} escolhidos
                {remaining > 0 && selectedCandidates.length > 0 ? ' · pode confirmar com menos' : ''}
              </span>
            </div>
          );
          return (
            <div className="vote-active">
              <div className="vote-ministry">
                <FontAwesomeIcon icon={faChurch} />
                <div>
                  <h2>{voting.name}</h2>
                  {voting.round > 1 && <span className="vote-round">Rodada {voting.round}</span>}
                </div>
              </div>
              {multi ? (
                <>
                  <p className="vote-instruction">Escolha até {maxVotes} nomes para {voting.seatsOpen} vaga(s). Os números são os mesmos do telão.</p>
                  {voting.candidates.length <= 6 && remainingBox}
                </>
              ) : (
                <p className="vote-instruction">Toque no nome de quem você quer votar:</p>
              )}
              <div className="vote-candidates">
                {voting.candidates.map((c, i) => {
                  const isSelected = selectedCandidates.includes(c._id);
                  const disabled = !isSelected && atLimit;
                  return (
                    <button
                      key={c._id}
                      className={`vote-candidate ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                      disabled={disabled}
                      onClick={() => toggleCandidate(c._id, maxVotes)}
                    >
                      <span className="vote-cand-num">{c.number || i + 1}</span>
                      <span className="vote-candidate-name">{c.name}</span>
                      {isSelected && <FontAwesomeIcon icon={faCheckCircle} />}
                    </button>
                  );
                })}
              </div>

              {/* Rodapé fixo: com listas longas o botão e o contador ficam sempre à vista */}
              <div className={`vote-submit-bar ${voting.candidates.length > 6 ? 'is-sticky' : ''}`}>
                {error && <p className="vote-error"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</p>}
                {multi && voting.candidates.length > 6 && remainingBox}
                <button
                  className="vote-btn vote-btn-primary vote-btn-block"
                  disabled={selectedCandidates.length === 0 || submitting}
                  onClick={handleVote}
                >
                  {submitting
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Enviando…</>
                    : <><FontAwesomeIcon icon={faVoteYea} /> {multi ? 'Confirmar votos' : 'Confirmar voto'}</>}
                </button>
              </div>
            </div>
          );
        })()}

        {/* Sair da assembleia */}
        <div className="vote-leave">
          {!confirmLeave ? (
            <button className="vote-leave-link" onClick={() => setConfirmLeave(true)}>
              <FontAwesomeIcon icon={faRightFromBracket} /> Sair da assembleia
            </button>
          ) : (
            <div className="vote-leave-confirm">
              <p>
                Você deixará de contar como presente.
                {voting && alreadyVoted ? ' Seu voto desta rodada continua valendo.' : ''}
                {' '}Use isto se for embora ou se for trocar de navegador.
              </p>
              {error && (!voting || alreadyVoted) && <p className="vote-error"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</p>}
              <div className="vote-leave-actions">
                <button className="vote-btn vote-btn-secondary" onClick={() => setConfirmLeave(false)} disabled={leaving}>
                  Cancelar
                </button>
                <button className="vote-btn vote-btn-danger" onClick={handleLeave} disabled={leaving}>
                  {leaving
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Saindo…</>
                    : <><FontAwesomeIcon icon={faRightFromBracket} /> Sair</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="vote-footer">Voto secreto e anônimo</p>
    </div>
  );
};

export default Vote;
